"use client";

import React, { useMemo, useState } from "react";
import LocalizedLink from "@/components/LocalizedLink";
import Image from "next/image";
import { ctaButtonClasses, CtaButtonIcon } from "@/components/ui/cta-button";
import { ClosingCtaBand } from "@/components/common/ClosingCtaBand";
import { instrumentSerif, interTight } from "@/components/home/v7/fonts";
import "@/components/home/v7/home-v7.css";

import {
  workHeroData,
  portfolioCategories,
  workCtaData,
  type WorkHeroData,
  type PortfolioCategory,
  type PortfolioItem,
} from "@/data/work";

import { useCurrentLocale } from "@/lib/useCurrentLocale";
import { getMessages } from "@/lib/messages";

// ---------------------------------------------------------------------------
// Header — same v7 hero shape as the homepage/TeamHero: grid backdrop + ember
// glow behind a two-phrase headline (plain + serif-italic accent).
// ---------------------------------------------------------------------------
const WorkHeader: React.FC<{ data: WorkHeroData }> = ({ data }) => {
  return (
    <section className="relative overflow-hidden pt-32 pb-16 md:pt-40">
      <div className="texture-glass absolute inset-0" aria-hidden="true" />
      <div className="v7-hero-glow" aria-hidden="true" />
      <div className="v7-wrap relative">
        <h1
          className="max-w-4xl text-balance font-medium leading-[1.04] tracking-[-0.03em]"
          style={{ fontSize: "clamp(2.4rem, 5.6vw, 4.6rem)" }}
        >
          <span className="v7-word">{data.title}</span>{" "}
          <span className="v7-word v7-serif">{data.boldTitle}</span>
        </h1>
        <p className="v7-rise-lcp mt-6 max-w-xl text-lg leading-relaxed text-foreground/60">
          {data.subtitle}
        </p>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Intro — short editorial block giving the hub page real body copy beyond
// the hero/case-study rows, for topical depth (SEO/GEO content-quality signal).
// ---------------------------------------------------------------------------
const WorkIntro: React.FC<{ intro?: { title: string; paragraphs: string[] } }> = ({ intro }) => {
  if (!intro || !intro.paragraphs?.length) return null;

  return (
    <section className="pb-16">
      <div className="v7-wrap max-w-3xl">
        <h2 className="v7-reveal text-2xl font-medium tracking-[-0.01em] md:text-3xl">
          {intro.title}
        </h2>
        <div className="mt-5 space-y-4">
          {intro.paragraphs.map((p, i) => (
            <p key={i} className="v7-reveal text-base leading-relaxed text-foreground/60 md:text-lg">
              {p}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Filter tabs — sticky hairline bar, orange underline on the active tab.
// ---------------------------------------------------------------------------
interface FilterTabsProps {
  categories: PortfolioCategory[];
  activeCategory: PortfolioCategory;
  onFilterChange: (category: PortfolioCategory) => void;
}
const FilterTabs: React.FC<FilterTabsProps> = ({
  categories,
  activeCategory,
  onFilterChange,
}) => {
  const locale = useCurrentLocale();
  const t = getMessages(locale);

  return (
    <div className="sticky top-0 z-20 border-y border-border bg-background">
      <nav className="v7-wrap flex flex-wrap gap-x-6 gap-y-1 overflow-x-auto">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            onClick={() => onFilterChange(category)}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium transition-colors duration-200 ${
              activeCategory === category
                ? "border-orange-500 text-orange-700 dark:text-orange-500"
                : "border-transparent text-foreground/60 hover:text-foreground"
            }`}
          >
            {t.workPage.categories[category] ?? category}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Case-study rows — hairline list, v7-tile thumbnail, shared secondary pill.
// ---------------------------------------------------------------------------
const CaseStudyRow: React.FC<{ item: PortfolioItem }> = ({ item }) => {
  const locale = useCurrentLocale();
  const t = getMessages(locale);

  return (
    <div className="v7-reveal border-t border-border last:border-b">
      <LocalizedLink
        href={`/case-studies/${item.slug}`}
        className="group grid grid-cols-1 items-center gap-6 py-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 md:grid-cols-12 md:gap-10"
      >
        <div className="md:col-span-7 lg:col-span-8">
          <h3 className="text-2xl font-medium tracking-[-0.01em] transition-colors duration-300 group-hover:text-orange-700 dark:group-hover:text-orange-500 md:text-3xl">
            {item.title}
          </h3>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-foreground/60">
            {item.description}
          </p>
          {item.tags && item.tags.length > 0 && (
            <p className="mt-4 text-sm text-foreground/60">
              {item.tags.join("  ·  ")}
            </p>
          )}
          <span className={ctaButtonClasses({ variant: "secondary", size: "sm", className: "mt-5" })}>
            {t.workPage.learnMore}
            <CtaButtonIcon />
          </span>
        </div>

        <div className="md:col-span-5 lg:col-span-4">
          <div className="v7-tile relative aspect-[16/10] overflow-hidden rounded-3xl p-3">
            <Image
              src={item.imageUrl}
              alt={`${item.title} — ${item.categories.join(", ")} project case study`}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="object-contain p-2 transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
        </div>
      </LocalizedLink>
    </div>
  );
};

interface CaseStudyListProps {
  items: PortfolioItem[];
  activeCategory: PortfolioCategory;
}
const CaseStudyList: React.FC<CaseStudyListProps> = ({ items, activeCategory }) => {
  const filteredItems = useMemo(() => {
    if (activeCategory === "ALL") return items;
    return items.filter((item) => item.categories.includes(activeCategory));
  }, [items, activeCategory]);

  return (
    <section className="relative z-10 py-4">
      <div className="v7-wrap">
        {filteredItems.map((item) => (
          <CaseStudyRow key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
};

interface WorkPageContentProps {
  items: PortfolioItem[];
}

export default function WorkPageContent({ items }: WorkPageContentProps) {
  const [activeCategory, setActiveCategory] =
    useState<PortfolioCategory>("ALL");
  const locale = useCurrentLocale();
  const t = getMessages(locale);

  const localizedHero = useMemo(() => {
    return {
      ...workHeroData,
      ...t.workPage.hero,
    };
  }, [t]);

  const localizedCta = useMemo(() => {
    return {
      ...workCtaData,
      title: t.workPage.cta.title,
      subtitle: t.workPage.cta.subtitle,
      buttonText: t.workPage.cta.buttonText,
    };
  }, [t]);

  const localizedItems = useMemo(() => {
    return items.map((item) => {
      const translated = t.workPage.projects[item.slug];
      return translated ? { ...item, ...translated } : item;
    });
  }, [items, t]);

  return (
    <main className={`home-v7 ${interTight.variable} ${instrumentSerif.variable} relative z-10 antialiased`}>
      <WorkHeader data={localizedHero} />
      <WorkIntro intro={t.workPage.intro} />
      <FilterTabs
        categories={portfolioCategories}
        activeCategory={activeCategory}
        onFilterChange={setActiveCategory}
      />
      <CaseStudyList items={localizedItems} activeCategory={activeCategory} />

      <ClosingCtaBand
        title={localizedCta.title}
        subtitle={localizedCta.subtitle}
        buttonText={localizedCta.buttonText}
        buttonHref={localizedCta.buttonHref}
      />
    </main>
  );
}
